import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Mail, Send, FileText, Paperclip, X, Upload, FolderOpen, Save, RotateCcw, Edit, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';

// Lazy load the RichTextEditor
const RichTextEditor = lazy(() => import('../ui/RichTextEditor'));

const Emails = () => {
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Template editing state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  const [formData, setFormData] = useState({
    to: [],
    subject: '',
    body: '',
    html_body: '',
    attachment_file_ids: []
  });
  const [selectedAttachments, setSelectedAttachments] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, filesRes, templatesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/files'),
        api.get('/notification-templates')
      ]);
      setClients(clientsRes.data);
      setFiles(filesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await api.put(`/notification-templates/${editingTemplate.template_key}`, {
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        html_body: editingTemplate.html_body
      });
      toast.success('Template saved successfully!');
      
      // Refresh templates
      const templatesRes = await api.get('/notification-templates');
      setTemplates(templatesRes.data);
      
      // Update selected template
      const updated = templatesRes.data.find(t => t.template_key === editingTemplate.template_key);
      if (updated) {
        setSelectedTemplate(updated);
        setEditingTemplate({ ...updated });
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateKey) => {
    try {
      await api.post(`/notification-templates/reset/${templateKey}`);
      toast.success('Template reset to default!');
      
      // Refresh templates
      const templatesRes = await api.get('/notification-templates');
      setTemplates(templatesRes.data);
      
      // Update editing template
      const updated = templatesRes.data.find(t => t.template_key === templateKey);
      if (updated) {
        setSelectedTemplate(updated);
        setEditingTemplate({ ...updated });
      }
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Failed to reset template');
    }
  };

  const handleUseTemplate = () => {
    if (!selectedTemplate) return;
    
    setFormData({
      ...formData,
      subject: selectedTemplate.subject,
      html_body: selectedTemplate.html_body,
      body: selectedTemplate.html_body.replace(/<[^>]*>/g, '')
    });
    toast.success('Template applied to compose form');
  };

  const handleFileSelect = (file) => {
    if (selectedAttachments.find(a => a.id === file.id)) {
      toast.info('File already attached');
      return;
    }

    setSelectedAttachments([...selectedAttachments, file]);
    setFormData({
      ...formData,
      attachment_file_ids: [...formData.attachment_file_ids, file.id]
    });
  };

  const handleRemoveAttachment = (fileId) => {
    setSelectedAttachments(selectedAttachments.filter(a => a.id !== fileId));
    setFormData({
      ...formData,
      attachment_file_ids: formData.attachment_file_ids.filter(id => id !== fileId)
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await api.post('/emails/upload-attachment', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded successfully');
      handleFileSelect(response.data);
      
      const filesRes = await api.get('/files');
      setFiles(filesRes.data);
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!formData.to.length || !formData.subject || (!formData.body && !formData.html_body)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/emails/send', formData);
      if (response.data.status === 'success' || response.data.status === 'partial') {
        toast.success(response.data.message);
        setFormData({ to: [], subject: '', body: '', html_body: '', attachment_file_ids: [] });
        setSelectedAttachments([]);
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Templates Sidebar */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-teal-600" />
          Email Templates
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Click to edit or use a template for composing emails.
        </p>
        
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {templates.map((template) => (
            <div
              key={template.template_key}
              className={`p-3 rounded-lg cursor-pointer transition-all ${
                selectedTemplate?.template_key === template.template_key
                  ? 'bg-teal-50 border-2 border-teal-500'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
              }`}
              onClick={() => handleTemplateSelect(template)}
            >
              <p className="font-medium text-gray-900 text-sm">{template.name}</p>
              <p className="text-xs text-gray-500 truncate">{template.subject}</p>
            </div>
          ))}
        </div>

        {/* Template Editor */}
        {editingTemplate && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900 flex items-center">
                <Edit className="w-4 h-4 mr-2" />
                Edit Template
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResetTemplate(editingTemplate.template_key)}
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Template Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditingTemplate(prev => ({ ...prev, name: newValue }));
                  }}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditingTemplate(prev => ({ ...prev, subject: newValue }));
                  }}
                  className="mt-1"
                />
              </div>

              {editingTemplate.placeholders && editingTemplate.placeholders.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-2">
                  <p className="text-xs text-blue-700 mb-1 font-medium">Placeholders:</p>
                  <div className="flex flex-wrap gap-1">
                    {editingTemplate.placeholders.map((p) => (
                      <code
                        key={p}
                        className="px-1.5 py-0.5 bg-white text-blue-600 rounded text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => {
                          navigator.clipboard.writeText(`[${p}]`);
                          toast.success(`Copied [${p}]`);
                        }}
                      >
                        [{p}]
                      </code>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Body (HTML)</Label>
                <div className="mt-1">
                  <Suspense fallback={<div className="h-32 bg-gray-100 rounded animate-pulse" />}>
                    <RichTextEditor
                      value={editingTemplate.html_body}
                      onChange={(value) => setEditingTemplate(prev => ({ ...prev, html_body: value }))}
                      placeholder="Template content..."
                      height="150px"
                    />
                  </Suspense>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  size="sm"
                >
                  {savingTemplate ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
                <Button
                  onClick={handleUseTemplate}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Use Template
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compose Email */}
      <div className="lg:col-span-2 glass rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Mail className="w-5 h-5 text-teal-600" />
          <h3 className="font-bold text-gray-900">Compose Email</h3>
        </div>

        <form onSubmit={handleSendEmail} className="space-y-4">
          <div>
            <Label htmlFor="recipients">Recipients *</Label>
            <Input
              id="recipients"
              data-testid="email-recipients-input"
              type="text"
              placeholder="Enter email addresses (comma-separated)"
              value={formData.to.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                to: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
          </div>

          <div>
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              data-testid="email-subject-input"
              type="text"
              placeholder="Email subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="body">Message *</Label>
            <div className="mt-1">
              <Suspense fallback={<div className="h-48 bg-gray-100 rounded-lg animate-pulse" />}>
                <RichTextEditor
                  value={formData.html_body}
                  onChange={(value) => setFormData({ ...formData, html_body: value, body: value.replace(/<[^>]*>/g, '') })}
                  placeholder="Write your message here..."
                  height="180px"
                />
              </Suspense>
            </div>
          </div>

          {/* Attachments Section */}
          <div>
            <Label>Attachments</Label>
            <div className="mt-2 space-y-2">
              {selectedAttachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Paperclip className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{attachment.filename}</span>
                    <span className="text-xs text-gray-500">
                      ({(attachment.file_size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFileSelector(true)}
                  className="flex-1"
                  size="sm"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Select from Files
                </Button>
                
                <input
                  type="file"
                  id="file-upload-email"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploadingFile}
                />
                <label htmlFor="file-upload-email" className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploadingFile}
                    className="w-full"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('file-upload-email').click();
                    }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingFile ? 'Uploading...' : 'Upload New'}
                  </Button>
                </label>
              </div>
            </div>
          </div>

          <Button
            data-testid="send-email-button"
            type="submit"
            disabled={sending}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </form>
      </div>

      {/* File Selector Dialog */}
      <Dialog open={showFileSelector} onOpenChange={setShowFileSelector}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Files to Attach</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {files.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No files available</p>
            ) : (
              files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors cursor-pointer hover:bg-gray-50 ${
                    selectedAttachments.find(a => a.id === file.id)
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200'
                  }`}
                  onClick={() => handleFileSelect(file)}
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-900">{file.filename}</p>
                      <p className="text-xs text-gray-500">
                        {(file.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  {selectedAttachments.find(a => a.id === file.id) && (
                    <div className="text-teal-600 font-medium text-sm">Selected</div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowFileSelector(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Emails;
